import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { WagmiProvider, useAccount } from 'wagmi'
import { QueryClientProvider } from '@tanstack/react-query'
import { wagmiAdapter, queryClient } from './config/appkit';
import { PaperWalletHandler } from './components/PaperWalletHandler';
import ErrorBoundary from './components/ErrorBoundary';
import logger from './utils/logger';
import { validateRequiredPublicRuntimeConfig } from './utils/runtimeConfig';
import { useSeoMeta } from './hooks/useSeoMeta';
import { resolvePortalAccess } from './utils/allowlist';
import footerImage from './assets/img/footer.png';
import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';

const getRuntimeConfigValidationError = (): string | null => {
  try {
    validateRequiredPublicRuntimeConfig({ requireAttestationSchemas: false });
    return null;
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Unknown runtime configuration error.';
    logger.error('Public runtime config validation failed', {
      message,
      error,
    });
    return message;
  }
};

const Home = lazy(() => import('./components/Home'));
const SignIn = lazy(() => import('./components/SignIn'));
const BuanAdminPanel = lazy(() => import('./components/BuanAdminPanel'));
const TownlandsAdmin = lazy(() => import('./components/TownlandsAdmin'));
const TownlandsDisplay = lazy(() => import('./components/TownlandsDisplay'));
const TownlandDetail = lazy(() => import('./components/TownlandDetail'));
const LearnMoreJoin = lazy(() => import('./components/LearnMoreJoin'));
const LearnMoreFoundingMembers = lazy(() => import('./components/LearnMoreFoundingMembers'));
const HowItWorks = lazy(() => import('./components/HowItWorks'));
const BusinessProfilePreview = lazy(() => import('./components/BusinessProfilePreview'));

const AllowlistedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { address, isConnected } = useAccount();
  const [accessStatus, setAccessStatus] = useState<'checking' | 'allowed' | 'denied'>('checking');

  useEffect(() => {
    if (!isConnected || !address) {
      setAccessStatus('denied');
      return;
    }

    let cancelled = false;
    setAccessStatus('checking');

    void resolvePortalAccess(address)
      .then((resolution) => {
        if (!cancelled) {
          setAccessStatus(resolution.allowed ? 'allowed' : 'denied');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAccessStatus('denied');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [address, isConnected]);

  if (!isConnected || !address || accessStatus === 'denied') {
    return <Navigate to="/signin?option=business" replace />;
  }

  if (accessStatus === 'checking') {
    return <div aria-live="polite">Checking portal access...</div>;
  }

  return <>{children}</>;
};

function AppContent() {
  const location = useLocation();
  const pathname = location.pathname;
  const isSignInPage = location.pathname === '/signin' || location.pathname === '/commonages';
  const isAdminPage = location.pathname === '/admin' || location.pathname === '/admin/townlands';
  const isLandingPage = location.pathname === '/';
  // Footer on /signin is rendered inside SignIn (same image as landing); hide global footer to avoid duplicate
  const shouldHideFooter = isSignInPage;

  useEffect(() => {
    // Add/remove class to body to control scrollbar visibility
    if (isLandingPage) {
      document.body.classList.add('no-scroll');
      document.documentElement.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
      document.documentElement.classList.remove('no-scroll');
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('no-scroll');
      document.documentElement.classList.remove('no-scroll');
    };
  }, [isLandingPage]);

  let seoTitle = 'Buan Fund';
  let seoDescription = 'Supporting biodiversity, wildlife stewardship, and funding for nature across Ireland.';
  let seoKeywords = 'Buan Fund, biodiversity, wildlife, stewardship, funding for nature, optimise for biodiversity';
  let seoRobots = 'index, follow';

  if (pathname === '/signin') {
    seoTitle = 'Sign In | Buan Fund';
    seoDescription = 'Landowner and business access for stewardship agreement signing and biodiversity participation.';
    seoKeywords = 'landowner signup, baseline stewardship agreement, biodiversity funding, conservation verification';
  } else if (pathname === '/commonages') {
    seoTitle = 'Commonages | Buan Fund';
    seoDescription = 'Explore Buan Fund commonage conservation areas, metrics, and area-specific stewardship warranties.';
    seoKeywords = 'commonages, Wicklow commonage restoration, biodiversity metrics, stewardship warranties';
  } else if (pathname === '/townlands') {
    seoTitle = 'Townlands | Buan Fund';
    seoDescription = 'Explore Wicklow and Ireland townlands in Buan Fund\'s biodiversity and wildlife conservation network.';
    seoKeywords = 'Wicklow areas, rewild Wicklow, wildlife corridor, biodiversity townlands, habitat restoration';
  } else if (pathname.startsWith('/townlands/')) {
    seoTitle = 'Townland Details | Buan Fund';
    seoDescription = 'View biodiversity, wildlife, and stewardship details for this conservation townland.';
    seoKeywords = 'townland biodiversity, wildlife stewardship, funding for nature, optimise for biodiversity';
  } else if (pathname === '/learn-more-join') {
    seoTitle = 'Join Buan Fund';
    seoDescription = 'Learn how landowners and partners join to support biodiversity, stewardship, and rewilding projects.';
    seoKeywords = 'money for rewilding, funding for nature, join biodiversity project, land stewardship';
  } else if (pathname === '/learn-more-founding-members') {
    seoTitle = 'Founding Members | Buan Fund';
    seoDescription = 'Discover founding member opportunities to fund wildlife and biodiversity restoration across Ireland.';
    seoKeywords = 'founding members, biodiversity funding, wildlife funding, stewardship support, HomeTree';
  } else if (pathname === '/how-it-works') {
    seoTitle = 'How It Works | Buan Fund';
    seoDescription = 'Learn how Buan Fund verifies stewards, sets area warranties, and streams biodiversity funding.';
    seoKeywords = 'how it works, biodiversity funding model, stewardship agreement, area warranties, conservation pools';
  } else if (pathname === '/business-profile-preview') {
    seoTitle = 'Business Profile Preview | Buan Fund';
    seoDescription = 'Preview how your business profile appears as a Buan Fund founding member.';
    seoKeywords = 'business profile preview, founding member profile, biodiversity platform profile';
  } else if (isAdminPage) {
    seoTitle = 'Admin | Buan Fund';
    seoDescription = 'Buan Fund administrative portal.';
    seoKeywords = 'admin';
    seoRobots = 'noindex, nofollow';
  }

  useSeoMeta({
    title: seoTitle,
    description: seoDescription,
    keywords: seoKeywords,
    path: pathname,
    robots: seoRobots
  });

  return (
    <>
      <div className="App">
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/commonages" element={<SignIn />} />
            <Route path="/admin" element={<BuanAdminPanel />} />
            <Route path="/admin/townlands" element={<TownlandsAdmin />} />
            <Route path="/townlands" element={<TownlandsDisplay />} />
            <Route path="/townlands/:id" element={<TownlandDetail />} />
            <Route path="/learn-more-join" element={<LearnMoreJoin />} />
            <Route
              path="/learn-more-founding-members"
              element={
                <AllowlistedRoute>
                  <LearnMoreFoundingMembers />
                </AllowlistedRoute>
              }
            />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/business-profile-preview" element={<BusinessProfilePreview />} />
          </Routes>
        </Suspense>
      </div>
      
      {/* Fixed Footer - Hidden on admin and sign-in (SignIn renders its own in-page footer) */}
      {!isAdminPage && !shouldHideFooter && (
        <footer className="fixed-footer">
          <img src={footerImage} alt="Buan Fund Footer" className="footer-image" />
        </footer>
      )}
    </>
  );
}

function App() {
  const runtimeConfigError = getRuntimeConfigValidationError();

  useEffect(() => {
    // Initialize the modal to ensure wagmi sync
    logger.log('Initializing AppKit modal for wagmi sync...');
  }, []);

  if (runtimeConfigError) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f6f8f7',
          padding: '24px',
        }}
      >
        <div
          style={{
            maxWidth: '760px',
            background: '#ffffff',
            border: '1px solid #d7ddda',
            borderRadius: '10px',
            padding: '20px 24px',
            color: '#1f2933',
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
          }}
        >
          <h1 style={{ margin: '0 0 10px 0', fontSize: '1.25rem' }}>Runtime Configuration Error</h1>
          <p style={{ margin: '0 0 12px 0' }}>
            The app could not start because required public environment variables are missing or invalid.
          </p>
          <pre
            style={{
              margin: 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              background: '#f3f5f4',
              borderRadius: '6px',
              padding: '12px',
              border: '1px solid #e2e8e5',
              color: '#8b1f1f',
            }}
          >
            {runtimeConfigError}
          </pre>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <WagmiProvider config={wagmiAdapter.wagmiConfig}>
        <QueryClientProvider client={queryClient}>
          <PaperWalletHandler />
          <Router>
            <AppContent />
          </Router>
        </QueryClientProvider>
      </WagmiProvider>
    </ErrorBoundary>
  );
}

export default App; 